import type { CSSProperties, FC, ReactNode } from 'react'

import { useState } from 'react'

import { activateOnKey } from '@/lib/a11y'

interface SliderProps {
    slides: ReactNode[]
    ariaLabel?: string
}

/*
  Drop-in replacement for the Webflow slider widget (it was driven by
  webflow.js, which we removed). Keeps the exact Webflow markup/classes so the
  styling is pixel-identical, and reproduces the behaviour in React: one slide
  visible at a time, left/right arrows with wrap-around, and clickable nav dots.
  Slides are inline-block at 100% width inside an overflow-hidden mask, so the
  whole strip is shifted with a single translateX, exactly like the original.
*/
export const Slider: FC<SliderProps> = ({ slides, ariaLabel }) => {
    const [index, setIndex] = useState(0)
    const count = slides.length

    const go = (next: number) => setIndex((next + count) % count)

    const slideStyle: CSSProperties = {
        transform: `translateX(-${index * 100}%)`,
        transition: 'transform 0.5s ease'
    }

    return (
        <div
            className='slider w-slider'
            role='region'
            aria-roledescription='carousel'
            aria-label={ariaLabel}
        >
            <div className='mask w-slider-mask'>
                {slides.map((slide, i) => (
                    <div key={i} className='slide w-slide' style={slideStyle}>
                        {slide}
                    </div>
                ))}
            </div>
            <div
                role='button'
                tabIndex={0}
                aria-label='Previous slide'
                onClick={() => go(index - 1)}
                onKeyDown={activateOnKey(() => go(index - 1))}
                className='left-arrow w-slider-arrow-left'
            >
                <div className='gradient-arrow-holder'>
                    <img src='/images/Left-Arrow.svg' loading='lazy' alt='' className='left-arrow-image' />
                </div>
            </div>
            <div
                role='button'
                tabIndex={0}
                aria-label='Next slide'
                onClick={() => go(index + 1)}
                onKeyDown={activateOnKey(() => go(index + 1))}
                className='right-arrow w-slider-arrow-right'
            >
                <div className='gradient-arrow-holder'>
                    <img src='/images/RIght-Arrow_1.svg' loading='lazy' alt='' className='left-arrow-image' />
                </div>
            </div>
            <div className='slide-nav w-slider-nav w-round'>
                {slides.map((_, i) => (
                    <div
                        key={i}
                        role='button'
                        tabIndex={0}
                        aria-label={`Show slide ${i + 1}`}
                        onClick={() => go(i)}
                        onKeyDown={activateOnKey(() => go(i))}
                        className={`w-slider-dot${i === index ? ' w-active' : ''}`}
                    />
                ))}
            </div>
        </div>
    )
}
